import React, { useMemo } from 'react';
import Accordion from '@components/catalog/accordion/accordion';
import { useTranslation } from 'next-i18next';
import { HotFilterGroupConfiguration } from '@config/catalog/hotfilter-group-configurations';
import useFilterListFacets from '@hooks/filters/use-filter-list-facets';
import FilterList from '@components/catalog/filter-group/components/filter-list/filter-list';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import { clsx } from 'clsx';
import SizeGuideFilter from '@components/catalog/size-guide-filter/size-guide-filter';
import styles from './size-hotfilter-accordion.module.scss';

interface SizeHotfilterAccordionProps {
  filterGroup: HotFilterGroupConfiguration;
}

const SizeHotfilterAccordion: React.FC<SizeHotfilterAccordionProps> = ({ filterGroup }) => {
  const { t } = useTranslation();
  const { filteredAndSortedFacets } = useFilterListFacets({
    filterGroupSortBy: filterGroup?.sortBy,
    filterGroup: filterGroup.facet,
  });
  const filters = useContextSelector(CatalogContext, (v) => v.filters ?? {});

  const selectionCount = useMemo(() => {
    const filterName = facetToFilterRename(filterGroup.facet);
    const appliedFilter = filters[filterName];
    return appliedFilter && Array.isArray(appliedFilter) ? appliedFilter.length : 0;
  }, [filterGroup, filters]);

  return (
    <Accordion
      header={
        <span
          className={clsx(
            styles.sizeHotfilterAccordion__title,
            selectionCount > 0 && styles['sizeHotfilterAccordion__title--selected'],
          )}
        >
          {t(filterGroup.label)}
          {selectionCount > 0 && (
            <span className={styles.sizeHotfilterAccordion__title__count}>({selectionCount})</span>
          )}
        </span>
      }
      key={filterGroup.label}
      a11yIdentifier={filterGroup.label}
      customClass={clsx(styles.sizeHotfilterAccordion)}
    >
      <div className={styles.sizeHotfilterAccordion__content}>
        <SizeGuideFilter
          sizeId={filterGroup.facet.substring(4)}
          universeId={filterGroup.universeId}
        />
        <FilterList
          filterGroup={filterGroup.facet}
          variant={'text-swatch'}
          facets={filteredAndSortedFacets}
          filterConfiguration={{
            type: 'text-swatch',
            label: filterGroup.label,
            itemText: filterGroup.itemText,
          }}
        />
      </div>
    </Accordion>
  );
};

export default SizeHotfilterAccordion;
